// CSV / TSV lead import for the B2B desk (Google Maps scrapers, Instagram exports, Excel pastes).

// Each field claims one column. Earlier aliases win; a whole-header match beats a single-word match,
// so "Designer" is never read as Instagram ("ig") and "Full name" never as an address ("full").
const FIELD_ALIASES = [
  ['email', ['email', 'emailaddress', 'emails', 'mail', 'email1'], ['email', 'emails', 'mail']],
  ['phone', ['phone', 'phonenumber', 'mobile', 'mobilenumber', 'whatsapp', 'whatsappnumber', 'telephone', 'contactnumber', 'tel', 'cell'], ['phone', 'mobile', 'whatsapp', 'telephone', 'tel', 'cell']],
  ['instagram', ['instagram', 'instagramhandle', 'instagramurl', 'ig', 'ighandle', 'insta', 'handle'], ['instagram', 'ig', 'insta']],
  ['website', ['website', 'websiteurl', 'web', 'site', 'domain', 'url'], ['website', 'web', 'site', 'domain']],
  ['country', ['country', 'countryname', 'nation'], ['country']],
  ['city', ['city', 'town', 'district', 'area', 'location'], ['city', 'town', 'district']],
  ['address', ['address', 'fulladdress', 'streetaddress', 'street', 'addr'], ['address', 'street']],
  ['brandName', ['brandname', 'brand', 'companyname', 'company', 'businessname', 'business', 'storename', 'store', 'shopname', 'shop', 'title', 'name'], ['brand', 'company', 'business', 'store', 'shop', 'title']],
  ['contactPerson', ['contactperson', 'contactname', 'contact', 'person', 'ownername', 'owner', 'fullname', 'firstname', 'designer', 'buyer'], ['contact', 'person', 'owner', 'designer']],
  ['niche', ['niche', 'category', 'categoryname', 'segment'], ['niche', 'category']],
  ['notes', ['notes', 'note', 'description', 'remarks', 'remark', 'comments'], ['notes', 'note', 'description', 'remarks']],
];

const INDIA_PLACES = /\b(india|delhi|new delhi|noida|gurugram|gurgaon|ghaziabad|faridabad|mumbai|thane|navi mumbai|pune|nagpur|jaipur|jodhpur|udaipur|surat|ahmedabad|vadodara|rajkot|bengaluru|bangalore|mysuru|mysore|kolkata|howrah|chennai|coimbatore|tiruppur|erode|madurai|hyderabad|secunderabad|kochi|cochin|lucknow|kanpur|varanasi|agra|meerut|indore|bhopal|chandigarh|ludhiana|amritsar|jalandhar|patna|bhubaneswar|guwahati|dehradun|raipur|ranchi|visakhapatnam|vijayawada)\b/i;
const UAE_PLACES = /\b(uae|united arab emirates|dubai|sharjah|abu dhabi|ajman|ras al khaimah|fujairah|al ain)\b/i;
const SAUDI_PLACES = /\b(saudi|ksa|riyadh|jeddah|dammam|khobar|mecca|makkah|medina|madinah)\b/i;

const COUNTRY_NAMES = [
  [/^(india|in|bharat)$/i, 'India'],
  [/^(uae|ae|united arab emirates|emirates)$/i, 'UAE'],
  [/^(saudi arabia|saudi|ksa|sa)$/i, 'Saudi Arabia'],
  [/^(kuwait|kw)$/i, 'Kuwait'],
  [/^(qatar|qa)$/i, 'Qatar'],
  [/^(uk|gb|united kingdom|great britain|england)$/i, 'UK'],
  [/^(usa|us|united states|united states of america)$/i, 'USA'],
  [/^(canada|ca)$/i, 'Canada'],
  [/^(australia|au)$/i, 'Australia'],
];

const DIAL_CODES = [['971', 'UAE'], ['966', 'Saudi Arabia'], ['965', 'Kuwait'], ['974', 'Qatar'], ['91', 'India'], ['44', 'UK'], ['61', 'Australia'], ['1', 'USA']];

const NICHE_KEYWORDS = [
  [/kaftan|abaya|jalabiya|modest/i, 'kaftan_modest'],
  [/kurti|ethnic|salwar|pret/i, 'kurti_ethnic'],
  [/resort|boho|beach|swim/i, 'resort_boho'],
  [/wholesale|trader|souk|distributor/i, 'wholesale_souk'],
  [/bridal|lehenga|wedding|occasion|gown/i, 'festive_bridal'],
  [/boutique|designer|clothing|fashion/i, 'domestic_boutique'],
];

function splitRows(rawText) {
  const delimiter = rawText.split(/\r?\n/, 1)[0].includes('\t') ? '\t' : ',';
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < rawText.length; i++) {
    const char = rawText[i];
    if (char === '"') {
      if (quoted && rawText[i + 1] === '"') { cell += '"'; i++; } else quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      row.push(cell.trim()); cell = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && rawText[i + 1] === '\n') i++;
      row.push(cell.trim()); cell = '';
      if (row.some(Boolean)) rows.push(row);
      row = [];
    } else cell += char;
  }
  row.push(cell.trim());
  if (row.some(Boolean)) rows.push(row);
  return { rows, unclosedQuote: quoted };
}

function headerTokens(header) {
  const words = String(header)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return { joined: words.join(''), words };
}

export function mapColumns(headers) {
  const parsed = headers.map(headerTokens);
  const claimed = new Set();
  const columns = {};
  for (const [field, joinedAliases, wordAliases] of FIELD_ALIASES) {
    let best = -1;
    let bestScore = 0;
    parsed.forEach(({ joined, words }, idx) => {
      if (claimed.has(idx)) return;
      const exact = joinedAliases.indexOf(joined);
      const word = wordAliases.findIndex((a) => words.includes(a));
      const score = exact >= 0 ? 200 - exact : word >= 0 ? 100 - word : 0;
      if (score > bestScore) { best = idx; bestScore = score; }
    });
    if (best >= 0) { columns[field] = best; claimed.add(best); }
  }
  return columns;
}

function normalizeCountry(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  const hit = COUNTRY_NAMES.find(([re]) => re.test(v));
  return hit ? hit[1] : v;
}

function countryFromPhone(phone) {
  const raw = String(phone || '').trim();
  // Only trust a dialling code that was written explicitly (+971… or 00971…).
  if (!/^(\+|00)/.test(raw)) return '';
  const digits = raw.replace(/\D/g, '').replace(/^00/, '');
  const hit = DIAL_CODES.find(([code]) => digits.startsWith(code));
  return hit ? hit[1] : '';
}

function countryFromPlace(text) {
  if (INDIA_PLACES.test(text)) return 'India';
  if (UAE_PLACES.test(text)) return 'UAE';
  if (SAUDI_PLACES.test(text)) return 'Saudi Arabia';
  return '';
}

function firstValidEmail(value) {
  const match = String(value || '').match(/[^\s@,;<>"']+@[^\s@,;<>"']+\.[a-z]{2,}/i);
  return match ? match[0].toLowerCase() : '';
}

function cleanInstagram(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  const handle = v.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/^@/, '').split(/[/?#\s]/)[0];
  return handle ? `@${handle}` : '';
}

function cleanWebsite(value) {
  const v = String(value || '').trim();
  return /google\.[a-z.]+\/maps|maps\.app\.goo\.gl|instagram\.com|facebook\.com/i.test(v) ? '' : v;
}

function pickNiche(rawNiche, templates, fallback) {
  const v = String(rawNiche || '').trim();
  if (!v) return fallback;
  if (templates[v]) return v;
  const hit = NICHE_KEYWORDS.find(([re]) => re.test(v));
  return hit ? hit[1] : fallback;
}

/**
 * Returns { leads, columns, problem }. `columns` maps each detected field to its header so the
 * import preview can show what was matched.
 */
export function parseLeadsCSV(rawText, { templates, defaultNiche = 'kaftan_modest', defaultShipping = 'free' }) {
  if (!rawText || !rawText.trim()) return { leads: [], columns: {}, problem: '' };
  const { rows, unclosedQuote } = splitRows(rawText);
  if (unclosedQuote) return { leads: [], columns: {}, problem: 'A quoted cell is never closed — check the file for a stray " character.' };
  if (rows.length < 2) return { leads: [], columns: {}, problem: 'Add a header row and at least one lead row.' };

  const headers = rows[0];
  const columnIdx = mapColumns(headers);
  const columns = Object.fromEntries(Object.entries(columnIdx).map(([field, idx]) => [field, headers[idx]]));
  if (columnIdx.brandName === undefined) {
    return { leads: [], columns, problem: 'No brand / company / name column found. Rename the business-name column to "brandName".' };
  }
  const get = (cols, field) => (columnIdx[field] === undefined ? '' : String(cols[columnIdx[field]] || '').trim());
  const defaultMarket = templates[defaultNiche]?.market === 'domestic' ? 'domestic' : 'export';

  const leads = [];
  for (let i = 1; i < rows.length; i++) {
    const cols = rows[i];
    const brandName = get(cols, 'brandName');
    if (!brandName) continue;
    const city = get(cols, 'city');
    const address = get(cols, 'address');
    const phone = get(cols, 'phone');
    const country = normalizeCountry(get(cols, 'country'))
      || countryFromPhone(phone)
      || countryFromPlace(`${city} ${address}`)
      || (defaultMarket === 'domestic' ? 'India' : '');
    const market = country === 'India' ? 'domestic' : country ? 'export' : defaultMarket;
    const rawNiche = get(cols, 'niche');
    const niche = pickNiche(rawNiche, templates, market === 'domestic' && templates[defaultNiche]?.market === 'export' ? 'domestic_boutique' : defaultNiche);
    const template = templates[niche] || templates[market === 'domestic' ? 'domestic_boutique' : 'kaftan_modest'];
    const notes = [get(cols, 'notes'), rawNiche && !templates[rawNiche] ? `Category: ${rawNiche}` : ''].filter(Boolean).join(' · ');

    leads.push({
      market,
      brandName,
      contactPerson: get(cols, 'contactPerson'),
      country,
      city,
      address,
      phone,
      email: firstValidEmail(get(cols, 'email')),
      website: cleanWebsite(get(cols, 'website')),
      instagram: cleanInstagram(get(cols, 'instagram')),
      niche,
      shippingModel: defaultShipping,
      fabrics: template?.fabrics || [],
      stage: 'identified',
      awb: '',
      courier: market === 'domestic' ? 'DTDC' : 'Aramex',
      targetQty: market === 'domestic' ? '30m initial' : '50m initial',
      notes: notes || 'Imported via CSV',
      followUpDate: '',
    });
  }
  return { leads, columns, problem: leads.length ? '' : 'No rows with a brand name were found.' };
}
